import { useState } from 'react';
import { t, type Lang } from './i18n';

export interface CoverSectionProps {
  title: string;
  description?: string;
  eyebrowText?: string;
  coverImage?: string;
  onStart: () => void;
  progress?: number;
  isOgMode?: boolean;
  height: number;
  lang?: Lang;
  /** Sentence-Bloom: no subtitle on cover; solid black when no hero image. */
  isSentenceBloom?: boolean;
}

export function CoverSection({
  title,
  description,
  eyebrowText,
  coverImage,
  onStart,
  progress = 1,
  isOgMode = false,
  height,
  lang = 'de',
  isSentenceBloom = false,
}: CoverSectionProps) {
  const hasDescription =
    !isSentenceBloom &&
    typeof description === 'string' &&
    description.length > 0 &&
    description !== title;

  const showButton = !isOgMode && progress >= 1;

  const [coverImgFailed, setCoverImgFailed] = useState(false);
  const showCoverImage =
    Boolean(coverImage) && !coverImgFailed && !isOgMode;

  return (
    <section
      data-testid="cover-section"
      role={!isOgMode ? 'dialog' : undefined}
      aria-label={!isOgMode ? 'Story-Start-Gate' : undefined}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: `${height}px`,
        maxHeight: '100%',
        zIndex: 1002,
        background: isSentenceBloom && !showCoverImage
          ? '#000'
          : 'radial-gradient(ellipse at center, rgba(240, 40, 115, 0.18) 0%, transparent 60%), #000',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '32px 24px 40px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        transition: 'opacity 0.6s ease-out',
      }}
    >
      {showCoverImage && (
        <>
          <img
            src={coverImage}
            alt=""
            aria-hidden="true"
            onError={() => setCoverImgFailed(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0) 18%, rgba(0,0,0,0.42) 60%, rgba(0,0,0,0.86) 100%)',
            }}
          />
          {isSentenceBloom && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '38%',
                zIndex: 2,
                background:
                  'linear-gradient(to bottom, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 70%, transparent 100%)',
                pointerEvents: 'none',
              }}
            />
          )}
        </>
      )}
      <div
        style={{
          maxWidth: 480,
          textAlign: 'left',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {eyebrowText && (
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: 'var(--scrolly-color-action, #F02873)',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            {eyebrowText}
          </div>
        )}

        <h1
          style={{
            fontFamily:
              "var(--scrolly-font-display, 'Instrument Serif', Georgia, serif)",
            fontSize: 34,
            fontWeight: 500,
            color: '#fff',
            margin: 0,
            letterSpacing: '-2px',
            lineHeight: 0.95,
            textShadow: '0 2px 20px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </h1>

        {hasDescription && (
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.82)',
              marginTop: 16,
              marginBottom: 0,
            }}
          >
            {description}
          </p>
        )}

        {showButton && (
          <button
            type="button"
            onClick={onStart}
            data-testid="cover-start-button"
            style={{
              marginTop: 24,
              padding: '12px 28px',
              cursor: 'pointer',
              background: 'var(--scrolly-color-action, #F02873)',
              color: '#fff',
              border: 'none',
              borderRadius: 999,
              font: 'bold 14px var(--scrolly-font-ui, "DM Sans", -apple-system, system-ui, sans-serif)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: '0 4px 24px rgba(240, 40, 115, 0.4)',
              transition:
                'transform 200ms ease-out, box-shadow 200ms ease-out',
            }}
          >
            {t('cover.start', lang)}
          </button>
        )}

        {!isOgMode && progress < 1 && (
          <div
            style={{
              marginTop: 24,
              fontSize: 13,
              color: 'rgba(255,255,255,0.7)',
              letterSpacing: '0.5px',
            }}
          >
            {t('cover.loading', lang)} … {Math.round(progress * 100)}%
          </div>
        )}
      </div>
    </section>
  );
}
